import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { ChartData, ChartOptions } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { FiInfoIconMode } from '@fi-shared/info-icon/info-icon.component';
import { AppTextService } from '@fi-shared/app-text';
import {
    IChecklist2HistoryReportLineGraph,
    IChecklistV2ReportParameter,
    IChecklistV2ReportParameterFilter,
} from '@fi-shared/checklist/fi-checklist2-report/shared/fi-checklist2-report';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';
import {
    checklistHistoryChartExternalTooltipHandler,
    getCompletedPercent,
    getDateFormatOptionsByTimePeriodType,
    getDeviationsPercent,
    getFilledDataBorderColorFn,
    getYAxisTicksLabel,
    HistoryChecklistLineChartSettings,
    segmentDataSkippedFn,
} from '@fi-shared/checklist/fi-checklist2-report/components/checklist-history-report/checklist-details/unit-performance-metrics/history-checklist-chart-settings';
import { FiChecklist2ReportHistoryService } from '@fi-shared/checklist/fi-checklist2-report/services/fi-checklist2-report-history.service';
import { Observable, Subject } from 'rxjs';
import { FiChecklist2ReportData } from '@fi-shared/checklist/fi-checklist2-report/shared/fi-checklist2-report-data';
import { FiChecklist2ReportDataService } from '@fi-shared/checklist/fi-checklist2-report/services/fi-checklist2-report-data.service';

@Component({
    selector: 'fi-history-checklist-details-unit-performance-metrics',
    templateUrl: './unit-performance-metrics.component.html',
    styleUrls: ['./unit-performance-metrics.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-history-checklist-details-unit-performance-metrics' },
    standalone: false,
})
export class FiHistoryChecklistDetailsUnitPerformanceMetricsComponent implements OnInit, OnDestroy {
    @Input() reportParameter: IChecklistV2ReportParameter;
    @Input() reportData: FiChecklist2ReportData;
    @Input({ required: true }) currentFilter: IChecklistV2ReportParameterFilter;
    @Input({ required: true }) auditMode: boolean;
    @Input({ required: true }) filterChanged$: Observable<void>;

    @ViewChild('checklistReportChartElRef', { static: true }) checklistReportChartElRef: ElementRef<HTMLCanvasElement>;
    @ViewChild(BaseChartDirective, { static: true }) chart: BaseChartDirective;

    private _dataForLineGraph: IChecklist2HistoryReportLineGraph[] = [];
    chartData: ChartData;
    chartOptions: ChartOptions<'line'> = {
        animation: { duration: 1500 },
        interaction: { mode: 'index', intersect: false },
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            tooltip: {
                enabled: false,
                position: 'nearest',
                external: (context) => checklistHistoryChartExternalTooltipHandler(context, this._dataForLineGraph),
            },
            legend: { display: false },
        },
        scales: {
            y: {
                suggestedMin: 0,
                suggestedMax: 100,
                beginAtZero: true,
                title: { display: true },
                ticks: { callback: getYAxisTicksLabel },
            },
        },
    };

    constructor(
        private appTextService: AppTextService,
        private fiFormatDateService: FiFormatDateService,
        private fiChecklist2ReportHistoryService: FiChecklist2ReportHistoryService,
        private fiChecklist2ReportDataService: FiChecklist2ReportDataService,
        private cdr: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        this.loadDataForLineGraph();
        this.fiChecklist2ReportDataService.subscribeOnFilterOrDateRangeChanged(
            this.reportParameter.Id,
            this.ngOnDestroy$,
            () => this.loadDataForLineGraph(),
        );
    }

    private loadDataForLineGraph() {
        const checklistHistoryDateRange = this.fiChecklist2ReportDataService.getDateRange(this.reportParameter.Id);
        this.fiChecklist2ReportHistoryService
            .getDataForLineGraph(this.currentFilter, checklistHistoryDateRange, this.auditMode)
            .then((result) => {
                this._dataForLineGraph = result;
                this.buildCustomChart();
                this.cdr.detectChanges();
            });
    }

    private buildCustomChart() {
        this.chartOptions.scales.y.title.text = this.appTextService.get('common.completed');
        this.chartData = {
            labels: this.getChartLabels(),
            datasets: [
                {
                    data: this._dataForLineGraph.map(getCompletedPercent),
                    fill: true,
                    borderColor: HistoryChecklistLineChartSettings.completedLineBorderColor,
                    tension: HistoryChecklistLineChartSettings.lineTension,
                    pointBackgroundColor: HistoryChecklistLineChartSettings.completedPointBackgroundColor,
                    pointBorderColor: HistoryChecklistLineChartSettings.pointBorderColor,
                    pointBorderWidth: HistoryChecklistLineChartSettings.pointBorderWidth,
                    pointRadius: HistoryChecklistLineChartSettings.borderRadius,
                    pointHoverRadius: HistoryChecklistLineChartSettings.borderRadius,
                    backgroundColor: getFilledDataBorderColorFn,
                    segment: {
                        borderColor: (ctx) => segmentDataSkippedFn(ctx, 'rgb(0,0,0,0.2)'),
                        borderDash: (ctx) => segmentDataSkippedFn(ctx, [6, 6]),
                        backgroundColor: (ctx) => segmentDataSkippedFn(ctx, 'rgba(255, 255, 255, 0)'),
                    },
                    spanGaps: true,
                },
                {
                    data: this._dataForLineGraph.map(getDeviationsPercent),
                    fill: false,
                    borderColor: HistoryChecklistLineChartSettings.deviationLineBorderColor,
                    pointBackgroundColor: HistoryChecklistLineChartSettings.deviationPointBackgroundColor,
                    pointBorderColor: HistoryChecklistLineChartSettings.pointBorderColor,
                    pointBorderWidth: HistoryChecklistLineChartSettings.pointBorderWidth,
                    pointRadius: HistoryChecklistLineChartSettings.borderRadius,
                    pointHoverRadius: HistoryChecklistLineChartSettings.borderRadius,
                    segment: {
                        borderColor: (ctx) => segmentDataSkippedFn(ctx, 'rgb(0,0,0,0.2)'),
                        borderDash: (ctx) => segmentDataSkippedFn(ctx, [6, 6]),
                    },
                    spanGaps: true,
                    tension: HistoryChecklistLineChartSettings.lineTension,
                },
            ],
        };
    }

    private getChartLabels(): string[] {
        const dateRangeTimePeriodType = this.fiChecklist2ReportDataService.getDateRange(
            this.reportParameter.Id,
        ).TimePeriodType;
        const dateFormatOptions: Intl.DateTimeFormatOptions =
            getDateFormatOptionsByTimePeriodType(dateRangeTimePeriodType);
        return this._dataForLineGraph.map((dataItem: IChecklist2HistoryReportLineGraph) => {
            return this.fiFormatDateService.formatDate(dataItem.Date, dateFormatOptions, {
                showToday: false,
                showUTC: false,
            });
        });
    }

    protected readonly FiInfoIconMode = FiInfoIconMode;

    private ngOnDestroy$ = new Subject<void>();
    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
